/**
 * React-Query hooks for the per-tenant RoutingPolicy editor (ADR-126
 * Decision 4).
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getRoutingPolicies,
  getRoutingPolicyById,
  getRoutingPoliciesForSchool,
  createRoutingPolicy,
  updateRoutingPolicy,
  deleteRoutingPolicy,
  type RoutingPolicyFilters,
  type RoutingPolicyWriteInput,
} from '../../services';

export const routingPolicyKeys = {
  all: ['routingPolicies'] as const,
  lists: () => [...routingPolicyKeys.all, 'list'] as const,
  list: (filters: RoutingPolicyFilters) =>
    [...routingPolicyKeys.lists(), filters] as const,
  details: () => [...routingPolicyKeys.all, 'detail'] as const,
  detail: (id: number) => [...routingPolicyKeys.details(), id] as const,
  forSchool: (schoolId: number) =>
    [...routingPolicyKeys.all, 'for-school', schoolId] as const,
};

export function useRoutingPolicies(filters: RoutingPolicyFilters) {
  return useQuery({
    queryKey: routingPolicyKeys.list(filters),
    queryFn: () => getRoutingPolicies(filters),
    placeholderData: (previousData) => previousData,
  });
}

export function useRoutingPolicy(id: number) {
  return useQuery({
    queryKey: routingPolicyKeys.detail(id),
    queryFn: () => getRoutingPolicyById(id),
    enabled: !!id,
  });
}

/** Site policies + inherited org defaults — for the inheritance-aware view. */
export function useRoutingPoliciesForSchool(schoolId: number | undefined) {
  return useQuery({
    queryKey: routingPolicyKeys.forSchool(schoolId ?? 0),
    queryFn: () => getRoutingPoliciesForSchool(schoolId as number),
    enabled: !!schoolId,
  });
}

export function useCreateRoutingPolicy() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: RoutingPolicyWriteInput) => createRoutingPolicy(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: routingPolicyKeys.all });
    },
  });
}

export function useUpdateRoutingPolicy() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: number;
      data: Partial<RoutingPolicyWriteInput>;
    }) => updateRoutingPolicy(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: routingPolicyKeys.all });
      queryClient.invalidateQueries({ queryKey: routingPolicyKeys.detail(id) });
    },
  });
}

export function useDeleteRoutingPolicy() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deleteRoutingPolicy(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: routingPolicyKeys.all });
    },
  });
}
